/**
 * Admin Lead Vault
 * 
 * System-wide view of all leads (routed, retry, stocked, etc.).
 * Table layout grouped by status with actions dropdown.
 */

import { useMemo, useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLeads } from "@/hooks/useLeadsDatabase";
import { useRoutingAttempts, useCreateRoutingAttempt } from "@/hooks/useRoutingAttempts";
import { useLeadSchemas } from "@/hooks/useLeadSchemas";
import { useQueryClient } from "@tanstack/react-query";
import { leadsQueryKeys } from "@/hooks/useLeadsDatabase";
import { routingAttemptsQueryKeys } from "@/hooks/useRoutingAttempts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RefreshCw, Search, Copy, MoreHorizontal, AlertTriangle, ChevronDown, BarChart3, Pencil, CreditCard } from "lucide-react";
import { DateRangeFilter, type DateRangeValue, type PresetKey } from "@/components/DateRangeFilter";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import type { LeadStatus } from "@/lib/leadStateMachine";
import { DEFAULT_LEAD_SCHEMAS } from "@/lib/leads/leadSchemas";
import { normalizeLeadTypeId, LEAD_TYPE_IDS } from "@/lib/leads/leadTypeIds";
import { supabase } from "@/integrations/supabase/client";
import { useMockAgents } from "@/contexts/MockAgentsContext";
import { useToast } from "@/hooks/use-toast";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
] as const;

const STATUS_OPTIONS: Array<{ label: string; value: "all" | LeadStatus }> = [
  { label: "All", value: "all" },
  { label: "Routed", value: "ROUTED" },
  { label: "Retry Queued", value: "RETRY_QUEUED" },
  { label: "Stocked", value: "STOCKED" },
  { label: "Unsold", value: "UNSOLD" },
  { label: "Review Required", value: "REVIEW_REQUIRED" },
  { label: "Credited", value: "CREDITED" },
];

// Status groups displayed in order
const STATUS_GROUP_ORDER: LeadStatus[] = [
  "ROUTED",
  "RETRY_QUEUED",
  "STOCKED",
  "ROUTING_IN_PROGRESS",
  "NEW",
  "UNSOLD",
  "REVIEW_REQUIRED",
  "CREDITED",
  "ARCHIVED",
];

// Key fields to show per lead type category
const KEY_FIELDS_MAP: Record<string, string[]> = {
  // Life insurance types
  [LEAD_TYPE_IDS.LIFE_FEX]: ["age_range", "monthly_budget", "health_status"],
  [LEAD_TYPE_IDS.LIFE_IUL]: ["age", "monthly_budget", "health_status"],
  [LEAD_TYPE_IDS.LIFE_MP]: ["mortgage_balance_est", "timeline", "health_status"],
  // Mortgage types
  [LEAD_TYPE_IDS.MORTGAGE_PURCHASE]: ["goal", "household_income", "credit_quality"],
  [LEAD_TYPE_IDS.MORTGAGE_REFI]: ["refi_goal", "household_income", "credit_quality"],
  [LEAD_TYPE_IDS.MORTGAGE_HELOC]: ["equity_goal", "household_income", "credit_quality"],
  [LEAD_TYPE_IDS.MORTGAGE_REVERSE]: ["equity_goal", "household_income", "credit_quality"],
};

const KEY_FIELD_LABELS: Record<string, string> = {
  age_range: "Age",
  age: "Age",
  monthly_budget: "Budget",
  health_status: "Health",
  credit_quality: "Credit",
  household_income: "Household Income",
  goal: "Goal",
  refi_goal: "Refi Goal",
  equity_goal: "Equity Goal",
  mortgage_balance_est: "Mortgage Balance",
  timeline: "Timeline",
};

function getKeyFieldsDisplay(leadTypeId: string, profile: Record<string, unknown>): string {
  const canonical = normalizeLeadTypeId(leadTypeId);
  const keys = KEY_FIELDS_MAP[canonical];
  if (!keys) return "—";

  const parts = keys.map((k) => {
    const val = profile[k];
    const label = KEY_FIELD_LABELS[k] || k;
    if (val === undefined || val === null || val === "") return `${label}: —`;
    return `${label}: ${String(val)}`;
  });
  return parts.join(" · ");
}

export default function AdminLeadVault() {
  const queryClient = useQueryClient();
  const { data: leads, isLoading } = useLeads();
  const { data: attempts } = useRoutingAttempts(200);
  const { data: schemas } = useLeadSchemas();
  const { agents } = useMockAgents();
  const { toast } = useToast();
  const createRoutingAttempt = useCreateRoutingAttempt();

  const [statusFilter, setStatusFilter] = useState<"all" | LeadStatus>("all");
  const [leadTypeFilter, setLeadTypeFilter] = useState<string>("all");
  const [stateFilter, setStateFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<DateRangeValue | undefined>(undefined);
  const [datePreset, setDatePreset] = useState<PresetKey | undefined>(undefined);
  const [drawerLeadId, setDrawerLeadId] = useState<string | null>(null);
  const [reassignLeadId, setReassignLeadId] = useState<string | null>(null);
  const [reassignAgentId, setReassignAgentId] = useState<string>("");
  const [reassignReason, setReassignReason] = useState<string>("");
  const [actionLoading, setActionLoading] = useState(false);
  // Edit Lead
  const [editLeadId, setEditLeadId] = useState<string | null>(null);
  const [editFields, setEditFields] = useState<Record<string, string>>({});
  const [editReason, setEditReason] = useState<string>("");
  // Credit Lead
  const [creditLeadId, setCreditLeadId] = useState<string | null>(null);
  const [creditReason, setCreditReason] = useState<string>("");
  const [creditNotes, setCreditNotes] = useState<string>("");
  const isMobile = useIsMobile();

  const schemaMap = useMemo(() => {
    const map = new Map<string, (typeof DEFAULT_LEAD_SCHEMAS)[number]>();
    for (const schema of DEFAULT_LEAD_SCHEMAS) {
      map.set(schema.leadTypeId, schema);
    }
    if (schemas) {
      for (const schema of schemas) {
        map.set(schema.leadTypeId, schema);
      }
    }
    return map;
  }, [schemas]);

  const attemptsByLead = useMemo(() => {
    const map = new Map<string, typeof attempts>();
    if (!attempts) return map;
    for (const attempt of attempts) {
      const existing = map.get(attempt.leadId) || [];
      existing.push(attempt);
      map.set(attempt.leadId, existing);
    }
    for (const [, list] of map) {
      list.sort((a, b) => b.attemptNumber - a.attemptNumber);
    }
    return map;
  }, [attempts]);

  // Extract unique lead types and states for filter dropdowns
  const { availableLeadTypes, availableStates } = useMemo(() => {
    if (!leads) return { availableLeadTypes: [], availableStates: [] };
    const types = new Set<string>();
    const states = new Set<string>();
    for (const lead of leads) {
      if (lead.leadTypeName) types.add(lead.leadTypeName);
      if (lead.state) states.add(lead.state);
    }
    return {
      availableLeadTypes: Array.from(types).sort(),
      availableStates: Array.from(states).sort(),
    };
  }, [leads]);

  const filtered = useMemo(() => {
    if (!leads) return [];
    return leads.filter((lead) => {
      if (statusFilter !== "all" && lead.status !== statusFilter) return false;
      if (leadTypeFilter !== "all" && lead.leadTypeName !== leadTypeFilter) return false;
      if (stateFilter !== "all" && lead.state !== stateFilter) return false;
      if (dateRange) {
        const leadTime = lead.createdAt;
        if (leadTime < dateRange.from.getTime() || leadTime > dateRange.to.getTime()) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const profile = (lead.leadProfile || {}) as Record<string, unknown>;
        const matchesId = lead.id.toLowerCase().includes(q);
        const matchesType = lead.leadTypeName.toLowerCase().includes(q);
        const matchesAgent = (lead.assignedAgentName || "").toLowerCase().includes(q);
        const matchesState = (lead.state || "").toLowerCase().includes(q);
        const matchesPhone = String(profile.phone || "").toLowerCase().includes(q);
        const matchesName = `${String(profile.first_name || "")} ${String(profile.last_name || "")}`.toLowerCase().includes(q);
        if (!matchesId && !matchesType && !matchesAgent && !matchesState && !matchesPhone && !matchesName) return false;
      }
      return true;
    });
  }, [leads, statusFilter, leadTypeFilter, stateFilter, search, dateRange]);

  // Pipeline summary counts (all leads, ignoring filters)
  const pipelineSummary = useMemo(() => {
    if (!leads) return [];
    const counts = new Map<string, number>();
    for (const lead of leads) {
      counts.set(lead.status, (counts.get(lead.status) || 0) + 1);
    }
    const ordered: Array<{ status: string; count: number }> = [];
    for (const status of STATUS_GROUP_ORDER) {
      const count = counts.get(status);
      if (count && count > 0) {
        ordered.push({ status, count });
      }
    }
    // Any remaining statuses not in the defined order
    for (const [status, count] of counts) {
      if (!STATUS_GROUP_ORDER.includes(status as LeadStatus) && count > 0) {
        ordered.push({ status, count });
      }
    }
    return ordered;
  }, [leads]);

  // Group filtered leads by status
  const groupedLeads = useMemo(() => {
    const groups = new Map<string, typeof filtered>();
    for (const lead of filtered) {
      const status = lead.status;
      const existing = groups.get(status) || [];
      existing.push(lead);
      groups.set(status, existing);
    }
    // Return in defined order
    const ordered: Array<{ status: string; leads: typeof filtered }> = [];
    for (const status of STATUS_GROUP_ORDER) {
      const list = groups.get(status);
      if (list && list.length > 0) {
        ordered.push({ status, leads: list });
      }
    }
    // Any remaining statuses
    for (const [status, list] of groups) {
      if (!STATUS_GROUP_ORDER.includes(status as LeadStatus) && list.length > 0) {
        ordered.push({ status, leads: list });
      }
    }
    return ordered;
  }, [filtered]);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: leadsQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: routingAttemptsQueryKeys.all });
  };

  const maskEmail = (value: string) => {
    const [user, domain] = value.split("@");
    if (!domain) return value;
    const maskedUser = user.length <= 2 ? `${user[0] ?? ""}*` : `${user[0]}***${user[user.length - 1]}`;
    return `${maskedUser}@${domain}`;
  };

  const maskPhone = (value: string) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length < 4) return value;
    return `***-***-${digits.slice(-4)}`;
  };

  const formatValue = (key: string, value: unknown) => {
    if (value === undefined || value === null || value === "") return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    const asString = String(value);
    if (key === "email") return maskEmail(asString);
    if (key === "phone") return maskPhone(asString);
    return asString;
  };

  const PRIMARY_FIELDS = ["first_name", "last_name", "phone", "email", "state"];
  const TRAILING_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "otp_verified"];

  const orderFields = (fields: typeof DEFAULT_LEAD_SCHEMAS[number]["fields"]) => {
    return [...fields].sort((a, b) => {
      const aPrimary = PRIMARY_FIELDS.indexOf(a.key);
      const bPrimary = PRIMARY_FIELDS.indexOf(b.key);
      const aTrailing = TRAILING_FIELDS.indexOf(a.key);
      const bTrailing = TRAILING_FIELDS.indexOf(b.key);

      const aGroup = aPrimary !== -1 ? 0 : aTrailing !== -1 ? 2 : 1;
      const bGroup = bPrimary !== -1 ? 0 : bTrailing !== -1 ? 2 : 1;
      if (aGroup !== bGroup) return aGroup - bGroup;

      if (aGroup === 0 && aPrimary !== bPrimary) return aPrimary - bPrimary;
      if (aGroup === 2 && aTrailing !== bTrailing) return aTrailing - bTrailing;

      return a.label.localeCompare(b.label);
    });
  };

  const formatTimestamp = (value?: string | number) => {
    if (!value) return "—";
    const date = typeof value === "number" ? new Date(value) : new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString();
  };

  const formatTimestampShort = (value?: string | number) => {
    if (!value) return "—";
    const date = typeof value === "number" ? new Date(value) : new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const drawerLead = useMemo(() => {
    if (!drawerLeadId || !leads) return null;
    return leads.find((lead) => lead.id === drawerLeadId) ?? null;
  }, [drawerLeadId, leads]);

  const reassignLead = useMemo(() => {
    if (!reassignLeadId || !leads) return null;
    return leads.find((lead) => lead.id === reassignLeadId) ?? null;
  }, [reassignLeadId, leads]);

  // Check if selected agent is eligible for the lead being reassigned
  const reassignAgentEligibility = useMemo(() => {
    if (!reassignLead || !reassignAgentId) return null;
    const agent = agents.find((a) => a.id === reassignAgentId);
    if (!agent) return null;

    const warnings: string[] = [];
    const leadVertical = reassignLead.vertical;
    if (agent.vertical !== leadVertical) {
      warnings.push(`Agent vertical (${agent.vertical}) doesn't match lead vertical (${leadVertical})`);
    }
    const leadState = (reassignLead.leadProfile as Record<string, unknown>)?.state as string | undefined;
    if (leadState && agent.licensedStates && !agent.licensedStates.includes(leadState)) {
      warnings.push(`Agent is not licensed in ${leadState}`);
    }
    if (!agent.isActive) {
      warnings.push("Agent is currently inactive");
    }
    return warnings.length > 0 ? warnings : null;
  }, [reassignLead, reassignAgentId, agents]);

  const recordAdminAttempt = async ({
    leadId,
    result,
    agentId,
    agentName,
    reason,
  }: {
    leadId: string;
    result: "ROUTED" | "STOCKED" | "CREDITED";
    agentId?: string;
    agentName?: string;
    reason: string;
  }) => {
    const lastAttempt = attemptsByLead.get(leadId)?.[0];
    const attemptNumber = (lastAttempt?.attemptNumber ?? 0) + 1;
    const now = Date.now();
    await createRoutingAttempt.mutateAsync({
      id: crypto.randomUUID(),
      leadId,
      attemptNumber,
      source: "LIVE",
      result,
      agentId,
      agentName,
      chargedAmount: undefined,
      scoring: { priorityScore: 0, bidScore: 0, qualityScore: 0, finalScore: 0 },
      eligibleAgentsCount: 0,
      ineligibleAgentsCount: 0,
      totalAgentsEvaluated: 0,
      guardrailsApplied: false,
      devOverridesApplied: false,
      rejectionCode: result === "STOCKED" ? "ADMIN_STOCKED" : undefined,
      rejectionReason: reason,
      startedAt: now,
      completedAt: now,
      durationMs: 0,
    });
  };

  const handleStockLead = async (leadId: string) => {
    if (!leads) return;
    const lead = leads.find((item) => item.id === leadId);
    if (!lead) return;
    setActionLoading(true);
    try {
      const { error } = await supabase
        .from("leads")
        .update({
          status: "STOCKED",
          routed_to_agent_id: null,
          routed_to_agent_name: null,
          last_updated_at: Date.now(),
        })
        .eq("id", leadId);
      if (error) throw error;

      await recordAdminAttempt({
        leadId,
        result: "STOCKED",
        reason: "Admin stocked lead",
      });

      toast({
        title: "Lead stocked",
        description: "Lead moved to stock and unassigned.",
      });
      handleRefresh();
    } catch (err) {
      console.error("[LeadVault] Failed to stock lead", err);
      toast({
        title: "Stock failed",
        description: "Could not stock this lead. Try again.",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenReassign = (leadId: string) => {
    setReassignLeadId(leadId);
    setReassignAgentId("");
    setReassignReason("");
  };

  // Fields that are read-only metadata (not editable)
  const READ_ONLY_KEYS = new Set(["utm_source", "utm_medium", "utm_campaign", "utm_content", "otp_verified"]);

  // Edit Lead
  const editLead = useMemo(() => {
    if (!editLeadId || !leads) return null;
    return leads.find((l) => l.id === editLeadId) ?? null;
  }, [editLeadId, leads]);

  const editLeadSchema = useMemo(() => {
    if (!editLead) return null;
    const canonicalId = normalizeLeadTypeId(editLead.leadTypeId);
    return schemaMap.get(canonicalId) || schemaMap.get(editLead.leadTypeId) || null;
  }, [editLead, schemaMap]);

  const editableFields = useMemo(() => {
    if (!editLeadSchema) return [];
    return editLeadSchema.fields.filter((f) => !READ_ONLY_KEYS.has(f.key));
  }, [editLeadSchema]);

  const handleOpenEditLead = (leadId: string) => {
    const lead = leads?.find((l) => l.id === leadId);
    if (!lead) return;
    const profile = (lead.leadProfile || {}) as Record<string, unknown>;
    // Pre-populate all editable fields from current profile
    const fields: Record<string, string> = {};
    const canonicalId = normalizeLeadTypeId(lead.leadTypeId);
    const schema = schemaMap.get(canonicalId) || schemaMap.get(lead.leadTypeId);
    if (schema) {
      for (const f of schema.fields) {
        if (!READ_ONLY_KEYS.has(f.key)) {
          fields[f.key] = profile[f.key] != null ? String(profile[f.key]) : "";
        }
      }
    }
    setEditLeadId(leadId);
    setEditFields(fields);
    setEditReason("");
  };

  // Check if state changed and show eligible agents
  const stateChanged = editLead ? editFields.state !== editLead.state && !!editFields.state : false;
  const editEligibleAgents = useMemo(() => {
    if (!editLead || !stateChanged || !editFields.state) return null;
    return agents.filter(
      (a) =>
        a.isActive &&
        a.licensedStates.includes(editFields.state) &&
        a.vertical === editLead.vertical
    );
  }, [editLead, stateChanged, editFields.state, agents]);

  // Compute what actually changed
  const editChanges = useMemo(() => {
    if (!editLead) return [];
    const profile = (editLead.leadProfile || {}) as Record<string, unknown>;
    const changes: Array<{ key: string; label: string; from: string; to: string }> = [];
    for (const field of editableFields) {
      const oldVal = profile[field.key] != null ? String(profile[field.key]) : "";
      const newVal = editFields[field.key] ?? "";
      if (oldVal !== newVal) {
        changes.push({ key: field.key, label: field.label, from: oldVal || "(empty)", to: newVal || "(empty)" });
      }
    }
    return changes;
  }, [editLead, editFields, editableFields]);

  const handleEditLeadSave = async () => {
    if (!editLead || !editReason.trim() || editChanges.length === 0) return;

    setActionLoading(true);
    try {
      // 1. Build updated lead_profile
      const updatedProfile = { ...(editLead.leadProfile || {}) } as Record<string, unknown>;
      for (const change of editChanges) {
        updatedProfile[change.key] = editFields[change.key];
      }

      const isCurrentlyRouted = editLead.status === "ROUTED" && editLead.assignedAgentId;
      const needsReroute = stateChanged;

      // 2. Build update payload
      const updatePayload: Record<string, unknown> = {
        lead_profile: updatedProfile,
        last_updated_at: Date.now(),
      };

      // Sync top-level state column if state changed
      if (stateChanged) {
        updatePayload.state = editFields.state;
        updatePayload.status = "NEW";
        updatePayload.agent_status = "new";
        updatePayload.disposition = null;

        // Clear agent assignment for re-routing
        if (isCurrentlyRouted) {
          updatePayload.routed_to_agent_id = null;
          updatePayload.routed_to_agent_name = null;
          updatePayload.owner_agent_id = null;
          updatePayload.owner_agent_email = null;
          updatePayload.agent_notes = null;
          updatePayload.agent_contacted_at = null;
          updatePayload.follow_up_at = null;
        }
      }

      const { error } = await supabase
        .from("leads")
        .update(updatePayload)
        .eq("id", editLead.id);
      if (error) throw error;

      // 3. If state changed and lead was routed, handle contract
      if (needsReroute && isCurrentlyRouted && editLead.contractId) {
        await supabase.rpc("increment_leads_delivered", {
          p_contract_id: editLead.contractId,
          p_count: -1,
        });
        await supabase.from("leads").update({ contract_id: null }).eq("id", editLead.id);
      }

      // 4. Record audit trail
      const changesSummary = editChanges.map((c) => `${c.label}: "${c.from}" → "${c.to}"`).join("; ");
      await recordAdminAttempt({
        leadId: editLead.id,
        result: needsReroute ? "STOCKED" : "ROUTED",
        reason: `Admin edit: ${changesSummary}. Reason: ${editReason.trim()}.${needsReroute ? " Lead reset to NEW for re-routing." : ""}`,
      });

      // 5. Trigger re-routing if state changed
      if (needsReroute) {
        supabase.functions.invoke("route-new-leads", {
          body: { leadIds: [editLead.id] },
        }).catch((err) => {
          console.error("[LeadVault] route-new-leads after edit failed:", err);
        });
      }

      toast({
        title: needsReroute ? "Lead updated & re-routing" : "Lead updated",
        description: `${editChanges.length} field${editChanges.length > 1 ? "s" : ""} updated.${needsReroute ? " Lead is being re-routed." : ""}`,
      });
      setEditLeadId(null);
      handleRefresh();
    } catch (err) {
      console.error("[LeadVault] Failed to edit lead", err);
      toast({
        title: "Edit failed",
        description: "Could not update lead. Try again.",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Credit Lead
  const creditLead = useMemo(() => {
    if (!creditLeadId || !leads) return null;
    return leads.find((l) => l.id === creditLeadId) ?? null;
  }, [creditLeadId, leads]);

  const handleOpenCreditLead = (leadId: string) => {
    setCreditLeadId(leadId);
    setCreditReason("");
    setCreditNotes("");
  };

  const handleCreditLead = async () => {
    if (!creditLead || !creditReason) return;
    setActionLoading(true);
    try {
      const adminUserId = (await supabase.auth.getUser()).data.user?.id ?? null;

      // Single atomic RPC: updates lead, decrements contract + agent caps, audit trail, notification
      const { data, error } = await supabase.rpc("credit_lead_admin", {
        p_lead_id: creditLead.id,
        p_credit_reason: creditReason,
        p_credit_notes: creditNotes.trim() || null,
        p_admin_user_id: adminUserId,
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string } | null;
      if (!result?.success) {
        toast({
          title: "Credit failed",
          description: result?.error || "Lead could not be credited. It may have already been credited or archived.",
          variant: "destructive",
        });
        setCreditLeadId(null);
        handleRefresh();
        return;
      }

      toast({
        title: "Lead credited",
        description: "Lead credited back. Contract counter and agent caps decremented.",
      });
      setCreditLeadId(null);
      handleRefresh();
    } catch (err) {
      console.error("[LeadVault] Failed to credit lead", err);
      toast({
        title: "Credit failed",
        description: "Could not credit this lead. Try again.",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReassign = async () => {
    if (!reassignLead || !reassignAgentId) return;
    const agent = agents.find((a) => a.id === reassignAgentId);
    if (!agent) return;
    if (!reassignReason.trim()) {
      toast({
        title: "Reason required",
        description: "Please provide a reason for reassigning this lead.",
        variant: "destructive",
      });
      return;
    }

    setActionLoading(true);
    try {
      // 1. Find the best contract for the new agent + lead type
      const canonicalLeadTypeId = normalizeLeadTypeId(reassignLead.leadTypeId);
      const { data: agentContracts } = await supabase
        .from("contracts")
        .select("id, lead_types, status")
        .eq("agent_id", agent.id)
        .in("status", ["active", "at_risk"]);

      let newContractId: string | null = null;
      if (agentContracts && agentContracts.length > 0) {
        // Prefer contracts that explicitly cover this lead type
        const typeMatching = agentContracts.filter(
          (c) => Array.isArray(c.lead_types) && c.lead_types.length > 0 && c.lead_types.includes(canonicalLeadTypeId)
        );
        // Fall back to unrestricted contracts, then any contract
        const unrestricted = agentContracts.filter(
          (c) => !Array.isArray(c.lead_types) || c.lead_types.length === 0
        );
        const bestContract = typeMatching[0] || unrestricted[0] || agentContracts[0];
        newContractId = bestContract?.id ?? null;
      }

      const oldContractId = reassignLead.contractId ?? null;
      const oldAgentId = reassignLead.assignedAgentId ?? null;

      // 2. Update lead with full ownership fields + reset CRM status
      const { error } = await supabase
        .from("leads")
        .update({
          status: "ROUTED",
          routed_to_agent_id: agent.id,
          routed_to_agent_name: agent.name,
          owner_agent_id: agent.id,
          owner_agent_email: agent.email || null,
          contract_id: newContractId,
          agent_status: "new",
          disposition: null,
          agent_notes: null,
          agent_contacted_at: null,
          follow_up_at: null,
          last_updated_at: Date.now(),
        })
        .eq("id", reassignLead.id);
      if (error) throw error;

      // 3. Record admin routing attempt
      await recordAdminAttempt({
        leadId: reassignLead.id,
        result: "ROUTED",
        agentId: agent.id,
        agentName: agent.name,
        reason: `Admin reassigned: ${reassignReason.trim()}`,
      });

      // 4. Update contract delivery counters
      if (newContractId) {
        await supabase.rpc("increment_leads_delivered", {
          p_contract_id: newContractId,
          p_count: 1,
        });
      }
      // Decrement old contract if it was different
      if (oldContractId && oldContractId !== newContractId) {
        await supabase.rpc("increment_leads_delivered", {
          p_contract_id: oldContractId,
          p_count: -1,
        });
      }

      // 5. Update agent lead counters (daily/monthly caps)
      await supabase.rpc("increment_agent_lead_counters", {
        p_agent_id: agent.id,
      });
      // Decrement old agent's counters if reassigning to a different agent
      if (oldAgentId && oldAgentId !== agent.id) {
        await supabase.rpc("increment_agent_lead_counters", {
          p_agent_id: oldAgentId,
          p_daily_increment: -1,
          p_monthly_increment: -1,
        });
      }

      // 6. Notify the new agent (fire-and-forget)
      const profile = (reassignLead.leadProfile || {}) as Record<string, unknown>;
      const leadFirst = String(profile.first_name || "").trim();
      const leadLast = String(profile.last_name || "").trim();
      const leadName = (leadFirst || leadLast) ? `${leadFirst} ${leadLast}`.trim() : "Unknown";
      const leadPhone = String(profile.phone || "");

      supabase.functions.invoke("notify-agent", {
        body: {
          agentId: agent.id,
          leadId: reassignLead.id,
          leadTypeName: reassignLead.leadTypeName,
          leadState: reassignLead.state,
          leadName,
          leadPhone,
        },
      }).catch((err) => {
        console.error("[LeadVault] notify-agent failed for reassignment:", err);
      });

      toast({
        title: "Lead reassigned",
        description: `Assigned to ${agent.name}.`,
      });
      handleRefresh();
      setReassignLeadId(null);
    } catch (err) {
      console.error("[LeadVault] Failed to reassign lead", err);
      toast({
        title: "Reassign failed",
        description: "Could not reassign this lead. Try again.",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const statusLabel = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Lead Vault</h1>
            <p className="text-sm text-muted-foreground">
              System-wide inventory of all leads across all statuses
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh} className="self-start">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Filters</CardTitle>
            <CardDescription>Find leads by status, ID, type, or assigned agent</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <div className="w-full sm:w-40">
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-40">
              <Select value={leadTypeFilter} onValueChange={setLeadTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Lead Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {availableLeadTypes.map((lt) => (
                    <SelectItem key={lt} value={lt}>{lt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-28">
              <Select value={stateFilter} onValueChange={setStateFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="State" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {availableStates.map((st) => (
                    <SelectItem key={st} value={st}>{st}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-0">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Search by name, phone, lead ID, type, agent, or state"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="w-full pt-1">
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                Date Range
              </label>
              <DateRangeFilter
                value={dateRange}
                onChange={setDateRange}
                activePreset={datePreset}
                onPresetChange={setDatePreset}
              />
            </div>
          </CardContent>
        </Card>

        {/* Pipeline Summary */}
        {!isLoading && pipelineSummary.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base">Pipeline Summary</CardTitle>
              </div>
              <CardDescription>
                {leads?.length ?? 0} total leads across all statuses
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {pipelineSummary.map(({ status, count }) => {
                  const isActive = statusFilter === status;
                  return (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(isActive ? "all" : status as any)}
                      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors hover:bg-accent ${
                        isActive ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-border"
                      }`}
                    >
                      <span className="text-muted-foreground">{statusLabel(status)}</span>
                      <span className="font-mono font-semibold">{count}</span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Leads</CardTitle>
            <CardDescription>
              {isLoading ? "Loading..." : `Showing ${filtered.length} leads`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Loading leads...</div>
            ) : filtered.length === 0 ? (
              <div className="text-sm text-muted-foreground">No leads found.</div>
            ) : (
              <div className="space-y-6">
                {groupedLeads.map(({ status, leads: groupLeads }) => (
                  <Collapsible key={status} defaultOpen={true} className="group/collapsible">
                    <CollapsibleTrigger className="flex items-center gap-2 mb-2 w-full cursor-pointer select-none">
                      <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=closed]/collapsible:-rotate-90" />
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {statusLabel(status)}
                      </h3>
                      <Badge variant="secondary" className="text-xs">
                        {groupLeads.length}
                      </Badge>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                    {isMobile ? (
                      <div className="space-y-2">
                        {groupLeads.map((lead) => {
                          const _profile = (lead.leadProfile || {}) as Record<string, unknown>;
                          return (
                            <div key={lead.id} className="rounded-md border p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm">{lead.leadTypeName}</span>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => setDrawerLeadId(lead.id)}>
                                      View Details
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleOpenReassign(lead.id)} disabled={actionLoading}>
                                      Reassign
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleOpenEditLead(lead.id)} disabled={actionLoading}>
                                      Edit Lead
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleStockLead(lead.id)} disabled={actionLoading}>
                                      Stock Lead
                                    </DropdownMenuItem>
                                    {lead.status === "ROUTED" && (
                                      <DropdownMenuItem onClick={() => handleOpenCreditLead(lead.id)} disabled={actionLoading} className="text-amber-600">
                                        Credit Lead
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                              <div className="flex flex-wrap items-center gap-1.5">
                                <Badge variant="outline" className="text-xs">{lead.state}</Badge>
                                <Badge variant={lead.status === "CREDITED" ? "outline" : "secondary"} className={`text-xs ${lead.status === "CREDITED" ? "border-amber-500 text-amber-700" : ""}`}>{lead.status}</Badge>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {lead.assignedAgentName ? (
                                  <span>Agent: {lead.assignedAgentName}</span>
                                ) : (
                                  <span>Unassigned</span>
                                )}
                                <span className="mx-1.5">·</span>
                                {formatTimestampShort(lead.createdAt)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                    <div className="rounded-md border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[140px]">Lead Type</TableHead>
                            <TableHead className="w-[90px]">State</TableHead>
                            <TableHead className="w-[110px]">Status</TableHead>
                            <TableHead>Key Fields</TableHead>
                            <TableHead className="w-[130px]">Agent</TableHead>
                            <TableHead className="w-[140px]">Created</TableHead>
                            <TableHead className="w-[60px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {groupLeads.map((lead) => {
                            const profile = (lead.leadProfile || {}) as Record<string, unknown>;
                            return (
                              <TableRow key={lead.id}>
                                <TableCell className="font-medium text-sm">
                                  {lead.leadTypeName}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="text-xs">{lead.state}</Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge variant={lead.status === "CREDITED" ? "outline" : "secondary"} className={`text-xs ${lead.status === "CREDITED" ? "border-amber-500 text-amber-700" : ""}`}>{lead.status}</Badge>
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground truncate max-w-[280px]">
                                  {getKeyFieldsDisplay(lead.leadTypeId, profile)}
                                </TableCell>
                                <TableCell className="text-sm">
                                  {lead.assignedAgentName || <span className="text-muted-foreground">—</span>}
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                  {formatTimestampShort(lead.createdAt)}
                                </TableCell>
                                <TableCell>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8">
                                        <MoreHorizontal className="h-4 w-4" />
                                        <span className="sr-only">Actions</span>
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => setDrawerLeadId(lead.id)}>
                                        View Details
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={() => handleOpenReassign(lead.id)}
                                        disabled={actionLoading}
                                      >
                                        Reassign
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={() => handleOpenEditLead(lead.id)}
                                        disabled={actionLoading}
                                      >
                                        Edit Lead
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={() => handleStockLead(lead.id)}
                                        disabled={actionLoading}
                                      >
                                        Stock Lead
                                      </DropdownMenuItem>
                                      {lead.status === "ROUTED" && (
                                        <DropdownMenuItem
                                          onClick={() => handleOpenCreditLead(lead.id)}
                                          disabled={actionLoading}
                                          className="text-amber-600"
                                        >
                                          Credit Lead
                                        </DropdownMenuItem>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                    )}
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lead Details Panel */}
      <Sheet open={!!drawerLeadId} onOpenChange={(open) => (!open ? setDrawerLeadId(null) : null)}>
        <SheetContent className="w-full sm:w-[520px] sm:max-w-[520px] overflow-y-auto">
          {drawerLead && (
            <>
              <SheetHeader>
                <SheetTitle>Lead Details</SheetTitle>
                <SheetDescription>
                  {drawerLead.leadTypeName} · {drawerLead.state} · {drawerLead.id}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Summary</CardTitle>
                    <CardDescription>
                      Routing status, assignment, and latest attempt
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-muted-foreground">Status</p>
                        <p className="font-medium">{drawerLead.status}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Assigned Agent</p>
                        <p className="font-medium">
                          {drawerLead.assignedAgentName ?? "Unassigned"}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Contract</p>
                        <p className="font-medium truncate">
                          {drawerLead.contractId ?? "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Created At</p>
                        <p className="font-medium">{formatTimestamp(drawerLead.createdAt)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Last Attempt</p>
                        <p className="font-medium">
                          {attemptsByLead.get(drawerLead.id)?.[0]?.result ?? "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Last Attempt At</p>
                        <p className="font-medium">
                          {formatTimestamp(attemptsByLead.get(drawerLead.id)?.[0]?.createdAt)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Lead Profile</CardTitle>
                    <CardDescription>
                      Normalized fields for routing and reporting
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      const canonicalLeadTypeId = normalizeLeadTypeId(drawerLead.leadTypeId);
                      const schema = schemaMap.get(canonicalLeadTypeId) || schemaMap.get(drawerLead.leadTypeId);
                      const profile = drawerLead.leadProfile || {};

                      if (!schema) {
                        return <div className="text-muted-foreground">No schema configured for this lead type.</div>;
                      }

                      return (
                        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                          {orderFields(schema.fields).map((field) => (
                            <div key={field.key}>
                              <p className="text-muted-foreground text-sm">{field.label}</p>
                              <p className="font-medium text-sm break-all">
                                {formatValue(field.key, (profile as Record<string, unknown>)[field.key])}
                              </p>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Raw Payload</CardTitle>
                    <CardDescription>
                      Original webhook payload (PII masked in UI)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <details className="group">
                      <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                        Expand raw payload
                      </summary>
                      <div className="mt-3 space-y-3">
                        <div className="flex justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const raw = JSON.stringify(drawerLead.leadPayload || {}, null, 2);
                              navigator.clipboard?.writeText(raw);
                            }}
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Copy Raw
                          </Button>
                        </div>
                        <div className="rounded-md border bg-muted/30 p-3 text-xs whitespace-pre-wrap max-h-[400px] overflow-y-auto">
                          {JSON.stringify(drawerLead.leadPayload || {}, null, 2)}
                        </div>
                      </div>
                    </details>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Reassign Dialog */}
      <Dialog open={!!reassignLeadId} onOpenChange={(open) => (!open ? setReassignLeadId(null) : null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Lead</DialogTitle>
            <DialogDescription>
              Assign this lead to another agent and record a reason for audit.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Agent</label>
              <Select value={reassignAgentId} onValueChange={setReassignAgentId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name} · {agent.vertical === "life_insurance" ? "Life" : "Mortgage"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {reassignAgentEligibility && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  Eligibility Warning
                </div>
                {reassignAgentEligibility.map((warning, i) => (
                  <p key={i} className="text-xs text-destructive">{warning}</p>
                ))}
                <p className="text-xs text-muted-foreground mt-1">You can still proceed with the reassignment.</p>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Reason</label>
              <Input
                placeholder="e.g. Client requested reassignment"
                value={reassignReason}
                onChange={(e) => setReassignReason(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReassignLeadId(null)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleReassign} disabled={!reassignAgentId || actionLoading}>
              Confirm Reassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Lead Dialog */}
      <Dialog open={!!editLeadId} onOpenChange={(open) => (!open ? setEditLeadId(null) : null)}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4" />
              Edit Lead
            </DialogTitle>
            <DialogDescription>
              Edit lead profile fields. Changing the state will trigger automatic re-routing.
            </DialogDescription>
          </DialogHeader>

          {editLead && editLeadSchema && (
            <div className="space-y-4 overflow-y-auto flex-1 pr-1">
              {/* Lead info */}
              <div className="rounded-md border p-3 text-sm space-y-1">
                <p className="font-medium">{editLead.leadTypeName}</p>
                <p className="text-muted-foreground">
                  State: <span className="font-mono font-medium">{editLead.state}</span>
                  {" · "}Status: <span className="font-mono">{editLead.status}</span>
                </p>
                {editLead.assignedAgentName && (
                  <p className="text-muted-foreground">
                    Assigned to: <span className="font-medium">{editLead.assignedAgentName}</span>
                  </p>
                )}
              </div>

              {/* Editable fields */}
              <div className="grid grid-cols-2 gap-3">
                {editableFields.map((field) => (
                  <div key={field.key} className={field.key === "state" ? "col-span-1" : ""}>
                    <Label className="text-xs">{field.label}</Label>
                    {field.key === "state" ? (
                      <Select
                        value={editFields.state || ""}
                        onValueChange={(v) => setEditFields((prev) => ({ ...prev, state: v }))}
                      >
                        <SelectTrigger className="h-8 text-sm mt-1">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((st) => (
                            <SelectItem key={st} value={st}>{st}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        className="h-8 text-sm mt-1"
                        value={editFields[field.key] ?? ""}
                        onChange={(e) =>
                          setEditFields((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* State change: eligibility check */}
              {stateChanged && (
                <div className={`rounded-md border p-3 text-sm ${
                  editEligibleAgents && editEligibleAgents.length > 0
                    ? "border-green-200 bg-green-50"
                    : "border-destructive/30 bg-destructive/10"
                }`}>
                  {editEligibleAgents && editEligibleAgents.length > 0 ? (
                    <>
                      <p className="font-medium text-green-700">
                        {editEligibleAgents.length} eligible agent{editEligibleAgents.length > 1 ? "s" : ""} in {editFields.state}
                      </p>
                      <p className="text-xs text-green-600 mt-0.5">
                        {editEligibleAgents.map((a) => a.name).join(", ")}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 text-destructive font-medium">
                        <AlertTriangle className="h-4 w-4" />
                        No eligible agents in {editFields.state}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Lead will be queued for retry until an agent becomes available.
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* ROUTED + state change warning */}
              {stateChanged && editLead.status === "ROUTED" && editLead.assignedAgentName && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
                  <div className="flex items-center gap-2 text-amber-700 font-medium">
                    <AlertTriangle className="h-4 w-4" />
                    Lead is currently routed
                  </div>
                  <p className="text-xs text-amber-600 mt-1">
                    Changing state will unassign from {editLead.assignedAgentName} and decrement the contract counter.
                  </p>
                </div>
              )}

              {/* Changes summary */}
              {editChanges.length > 0 && (
                <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1">
                  <p className="font-medium text-sm">Changes ({editChanges.length})</p>
                  {editChanges.map((c) => (
                    <p key={c.key} className="text-muted-foreground">
                      <span className="font-medium text-foreground">{c.label}:</span>{" "}
                      <span className="line-through">{c.from}</span> → <span className="font-medium">{c.to}</span>
                    </p>
                  ))}
                  {stateChanged && (
                    <p className="text-amber-600 font-medium mt-1">
                      State changed — lead will be re-routed automatically.
                    </p>
                  )}
                </div>
              )}

              {/* Reason */}
              <div className="space-y-2">
                <Label>Reason for edit</Label>
                <Textarea
                  placeholder="e.g. Lead had typo in email, corrected state from survey error"
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              variant="outline"
              onClick={() => setEditLeadId(null)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditLeadSave}
              disabled={
                actionLoading ||
                editChanges.length === 0 ||
                !editReason.trim()
              }
            >
              {actionLoading ? "Saving..." : stateChanged ? "Save & Re-route" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit Lead Dialog */}
      <Dialog open={!!creditLeadId} onOpenChange={(open) => (!open ? setCreditLeadId(null) : null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-amber-600" />
              Credit Lead
            </DialogTitle>
            <DialogDescription>
              Credit this lead back — reverses the delivery charge on the agent's contract.
            </DialogDescription>
          </DialogHeader>

          {creditLead && (
            <div className="space-y-4">
              <div className="rounded-md border p-3 bg-muted/50 text-sm space-y-1">
                <p className="font-medium">{creditLead.leadTypeName}</p>
                <p className="text-xs text-muted-foreground">
                  State: <span className="font-mono font-medium">{creditLead.state}</span>
                  {" · "}Agent: <span className="font-medium">{creditLead.assignedAgentName || "Unknown"}</span>
                </p>
                {creditLead.contractId && (
                  <p className="text-xs text-muted-foreground">
                    Contract: <span className="font-mono">{creditLead.contractId.slice(0, 8)}...</span>
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Credit Reason *</Label>
                <Select value={creditReason} onValueChange={setCreditReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Invalid Lead">Invalid Lead</SelectItem>
                    <SelectItem value="Duplicate">Duplicate</SelectItem>
                    <SelectItem value="Wrong State/Territory">Wrong State/Territory</SelectItem>
                    <SelectItem value="Bad Contact Info">Bad Contact Info</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Additional details about the credit..."
                  value={creditNotes}
                  onChange={(e) => setCreditNotes(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="rounded-md border border-amber-500/30 bg-amber-50 dark:bg-amber-950/20 p-3 text-sm">
                <p className="font-medium text-amber-800 dark:text-amber-300">This action will:</p>
                <ul className="list-disc list-inside text-xs text-amber-700 dark:text-amber-400 mt-1 space-y-0.5">
                  <li>Mark lead as <span className="font-mono font-medium">CREDITED</span> (terminal state)</li>
                  <li>Decrement the contract delivery counter by 1</li>
                  <li>Notify the agent</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditLeadId(null)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleCreditLead}
              disabled={!creditReason || actionLoading}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {actionLoading ? "Crediting..." : "Confirm Credit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
